/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.signature.InvalidSignatureException
import com.ionspin.kotlin.crypto.signature.Signature
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import net.sergeych.bipack.decodeFromBipack
import net.sergeych.mp_tools.decodeBase64Url

/**
 * Public key to verify signatures only
 */
@Serializable
@SerialName("sigb")
class VerifyingPublicKey(override val keyBytes: UByteArray) : UniversalKey(), VerifyingKey {
    /**
     * Verify the signature and return true if it is correct.
     */
    override fun verify(signature: UByteArray, message: UByteArray): Boolean = try {
        Signature.verifyDetached(signature, message, keyBytes)
        true
    } catch (_: InvalidSignatureException) {
        false
    }

    @Transient
    override val magic: KeysmagicNumber = KeysmagicNumber.defaultVerifying

    override val id by lazy { KeyId(magic, keyBytes, null, true) }

    /**
     * Create a [Multikey] that requires presence of this or [other] key
     */
    infix fun or(other: VerifyingPublicKey) = Multikey(this) or other
    /**
     * Create a [Multikey] that requires presence of this or [other] key
     */
    infix fun or(other: SigningSecretKey) = Multikey(this) or other
    /**
     * Create a [Multikey] that requires presence of this or [other] key
     */
    infix fun or(other: Multikey) = Multikey(this) or other

    /**
     * Create a [Multikey] that requires presence of this and [other]
     */
    infix fun and(other: VerifyingPublicKey) = Multikey(this) and other
    /**
     * Create a [Multikey] that requires presence of this and [other]
     */
    infix fun and(other: SigningSecretKey) = Multikey(this) and other
    /**
     * Create a [Multikey] that requires presence of this and [other]
     */
    infix fun and(other: Multikey) = Multikey(this) and other

    companion object {
        /**
         * Parse any known public key text representation, including what [toString] return (for public keys it is
         * possible)
         * @throws IllegalArgumentException the public key isn't recognized, in particular [BinaryId.InvalidException]
         *      if the text is corrupt
         */
        fun parse(text: String): VerifyingPublicKey {
            val s = text.trim()

            fun parseId(t: String): VerifyingPublicKey {
                // assume it is an id:
                val id = BinaryId.restoreFromString(t)
                return if (id.magic == KeysmagicNumber.defaultVerifying.ordinal)
                    id.asVerifyingKey as VerifyingPublicKey
                else throw IllegalArgumentException("Invalid magick: ${id.magic} when parsing[$t]")
            }

            // 🗝sig#I1po9Y2I7p2aOxeh4nFyGPm3e0YunBEu1Mo-PmIqP84Evg
            return when {
                s.startsWith("\uD83D\uDDDDver#") -> parseId(s.drop(6))
                s.startsWith("ver#") -> parseId(s.drop(4))
                s.startsWith("#") -> parseId(s.drop(1))
                else -> {
                    // consider it is serialized key in base64 format
                    val data = s.decodeBase64Url().asUByteArray()
                    if (data.size == 32)
                        VerifyingPublicKey(data)
                    else if( data.size == 34) {
                        // raw id
                        BinaryId(data).asVerifyingKey as VerifyingPublicKey
                    }else {
                        runCatching { data.decodeFromBipack<VerifyingPublicKey>() }.getOrNull()
                            ?: kotlin.runCatching { data.decodeFromBipack<UniversalKey>() as VerifyingPublicKey }
                                .getOrElse { throw IllegalArgumentException("can't parse verifying key") }
                    }
                }
            }
        }
    }
}